import { getExt, getFileNameWithoutExt } from '@ps/utils/game'
import { ElUploadInternalFileDetail } from 'element-ui/types/upload'
import store from '@ps/store'
import gameIframe from '@ps/bridge/iframe/iframe'
import _ from 'lodash'
import i18n from '@ps/lang'
import { AssetCreateResponse } from '@ps/api/asset'
import validator, { ValidatorRule } from '@com/utils/file-validator'
import { maxVideoSize } from '@ps/utils/config'
import { VideoToProjectParams } from '@ps/api/editor/video'
import { libDataPool } from '@ps/bridge/resource/pool'

export function validate(file: File) {
  const fileType = ['mp4']

  const rules = [
    {
      validate: ({ file }: ValidatorRule) => {
        if (!fileType.includes(getExt(file.name.toLowerCase()))) {
          return {
            valid: false,
            message: `${i18n.t(
              'workbench.resourceManagers.videoUploader.uploadRule[0]',
            )} ${fileType.join('、')} ${i18n.t(
              'workbench.resourceManagers.videoUploader.uploadRule[1]',
            )}`,
          }
        }
        if (file.size / 1024 / 1024 > maxVideoSize) {
          return {
            valid: false,
            message: `${i18n.t(
              'workbench.resourceManagers.videoUploader.uploadRule[2]',
            )} ${maxVideoSize} M`,
          }
        }
        return {
          valid: true,
        }
      },
      file: file,
    },
  ]

  return validator(rules)
}

export async function addVideoToProjectStore(
  file: ElUploadInternalFileDetail,
): Promise<AssetCreateResponse> {
  const fileName = file.name.toLowerCase()
  const { valid, errors } = validate(file.raw)

  if (!valid) {
    // @ts-ignore
    throw new Error(`${errors[0].message}`)
  }

  const res = await store.dispatch(
    'workspace/resourceManager/createComponent',
    {
      data: {
        file: file.raw,
        name: fileName, // 保存原始名字到远端，保证从资源库新增资源时能够使用原有的名字
      },
      category: 'video',
    },
  )

  // 创建资源失败
  if (res.code !== 0) {
    throw new Error(res.msg)
  }
  return res.data
}

export async function addVideoToPlayable({
  isAdd,
  data,
  isAssetMode = false,
  isProjectOnly = false,
}: {
  isAdd: boolean
  data: AssetCreateResponse
  isAssetMode?: boolean
  isProjectOnly?: boolean
}): Promise<boolean> {
  // 必须等到游戏加载后才会添加到游戏中
  if (!gameIframe.iframe && !gameIframe.isGameReady) {
    return false
  }

  // 如果到达上限，不允许添加视频
  const limitStatus = await store.dispatch(
    'workspace/game/video/getUpperLimitStatus',
  )
  // 添加资源模式下，才限制视频
  if (isAdd && limitStatus) {
    throw new Error(
      i18n.t('workbench.resourceManagers.videoUploader.sceneSupport') as string,
    )
  }

  // 添加资源到项目
  const params: Partial<VideoToProjectParams> = {}
  params.asset_id = String(data.id)
  if (isAssetMode) {
    params.from = 3
  }
  const projectResourceResponse = await store.dispatch(
    'workspace/game/video/addVideoResourceToProject',
    params,
  )
  const originPath = _.get(projectResourceResponse, 'data.origin.path')
  const videoNodeConfig = {
    name: getFileNameWithoutExt(_.get(data, 'name').toLowerCase()),
    sourceVideoUrl: originPath || _.get(data, 'file_url'),
    videoCoverUrl: _.get(data, 'icon_url'),
    assetId: _.get(data, 'id'),
    width: _.get(data, 'extra.width')
      ? _.get(data, 'extra.width') // 有时extra里没有对应的信息，从其他兜底字段获取
      : _.get(data, 'width'),
    height: _.get(data, 'extra.height')
      ? _.get(data, 'extra.height')
      : _.get(data, 'height'),
    sourceVideoDuration: _.get(data, 'extra.duration')
      ? _.get(data, 'extra.duration')
      : _.get(data, 'duration'),
    videoUrl: _.get(projectResourceResponse, 'data.video.path'),
    audioUrl: _.get(projectResourceResponse, 'data.audio.path'),
    duration: _.get(projectResourceResponse, 'data.video.duration'),
    size: _.get(projectResourceResponse, 'data.video.file_size'),
  }
  // 记录到资源映射池，方便后续使用
  libDataPool.setLibData(videoNodeConfig.videoUrl, {
    name: videoNodeConfig.name,
    data: {
      ..._.omit(videoNodeConfig, ['name', 'assetId']),
    },
  })
  // 只保存资源到项目中
  if (isProjectOnly) return projectResourceResponse
  if (isAdd) {
    await store.dispatch('workspace/game/video/addVideoNode', videoNodeConfig)
  } else {
    await store.dispatch(
      'workspace/game/video/toggleVideoNode',
      videoNodeConfig,
    )
  }
  return true
}
